import { useEffect, useMemo, useState } from 'react';
import { ArrowLeft, Loader2, MessageSquare, X } from 'lucide-react';
import toast from 'react-hot-toast';
import { useAuth } from '@shared/authContext';
import MessageComposer from '@shared/messaging/MessageComposer';
import MessageThreadList from '@shared/messaging/MessageThreadList';
import MessageThreadView from '@shared/messaging/MessageThreadView';
import ServiceCallLinkPicker from '@shared/messaging/ServiceCallLinkPicker';
import {
  useEnsureMessageThread,
  useMessageThreadConversation,
  useMessageThreads,
} from '@shared/messaging/useMessaging';
import { useShell } from '../../shell/ShellContext';

const formatTechName = (technician) => {
  if (!technician) return 'Technician';
  return [technician.firstName, technician.lastName].filter(Boolean).join(' ').trim() || 'Technician';
};

function ThreadConversation({
  threadId,
  technician,
  serviceCallId,
  serviceCallLabel,
  onBack,
  onClose,
}) {
  const { user } = useAuth();
  const [linkedServiceCallId, setLinkedServiceCallId] = useState(serviceCallId);
  const [linkedServiceCallLabel, setLinkedServiceCallLabel] = useState(serviceCallLabel);

  const {
    messages,
    isLoading,
    isSending,
    sendMessage,
    markRead,
    markReadOnLoad,
  } = useMessageThreadConversation(threadId, {
    enabled: !!threadId,
    liveUpdates: true,
    markReadOnLoad: true,
  });

  useEffect(() => {
    setLinkedServiceCallId(serviceCallId || null);
    setLinkedServiceCallLabel(serviceCallLabel || null);
  }, [serviceCallId, serviceCallLabel, threadId]);

  useEffect(() => {
    if (!threadId || !markReadOnLoad) return;
    markRead().catch(() => {});
  }, [threadId, markRead, markReadOnLoad]);

  const handleSend = async ({ body, serviceCallId: callId }) => {
    try {
      await sendMessage({ body, serviceCallId: callId });
      if (callId) {
        setLinkedServiceCallId(null);
        setLinkedServiceCallLabel(null);
      }
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to send message');
    }
  };

  return (
    <>
      <header className="flex items-center justify-between px-4 py-3 border-b border-surface-border flex-shrink-0">
        <div className="flex items-center gap-2 min-w-0">
          {onBack && (
            <button
              type="button"
              onClick={onBack}
              className="p-1.5 rounded-md hover:bg-surface-hover text-gray-500"
              aria-label="Back to inbox"
            >
              <ArrowLeft className="h-4 w-4" />
            </button>
          )}
          <MessageSquare className="h-5 w-5 text-primary-600 flex-shrink-0" />
          <div className="min-w-0">
            <h2 className="text-sm font-semibold text-gray-900 truncate">
              {formatTechName(technician)}
            </h2>
            <p className="text-2xs text-gray-500">Direct message</p>
          </div>
        </div>
        <button
          type="button"
          onClick={onClose}
          className="p-1.5 rounded-md hover:bg-surface-hover text-gray-500"
          aria-label="Close messages"
        >
          <X className="h-4 w-4" />
        </button>
      </header>

      <MessageThreadView
        messages={messages}
        currentUserId={user?.id}
        isLoading={isLoading}
        showSenderNames
      />

      <MessageComposer
        onSend={handleSend}
        isSending={isSending}
        placeholder={`Message ${formatTechName(technician)}…`}
        serviceCallId={linkedServiceCallId}
        serviceCallLabel={linkedServiceCallLabel}
        onClearServiceCall={() => {
          setLinkedServiceCallId(null);
          setLinkedServiceCallLabel(null);
        }}
        linkPicker={(
          <ServiceCallLinkPicker
            technicianId={technician?.id}
            selectedCallId={linkedServiceCallId}
            selectedCallLabel={linkedServiceCallLabel}
            onSelect={({ serviceCallId: callId, serviceCallLabel: callLabel }) => {
              setLinkedServiceCallId(callId);
              setLinkedServiceCallLabel(callLabel);
            }}
            onClear={() => {
              setLinkedServiceCallId(null);
              setLinkedServiceCallLabel(null);
            }}
            disabled={!technician?.id}
          />
        )}
      />
    </>
  );
}

export default function MessagePanel() {
  const {
    messagePanel,
    closeMessagePanel,
    openMessageThread,
    openMessageInbox,
  } = useShell();

  const { data: threads = [], isLoading: threadsLoading } = useMessageThreads({
    enabled: messagePanel.open,
    refetchInterval: messagePanel.open ? 60000 : false,
  });

  const technicianId = messagePanel.technicianId;
  const showInbox = messagePanel.open && !technicianId;

  const selectedTechnician = useMemo(() => {
    if (messagePanel.technician) return messagePanel.technician;
    return threads.find((thread) => thread.technicianId === technicianId)?.technician || null;
  }, [messagePanel.technician, threads, technicianId]);

  const { data: ensuredThread, isLoading: ensureLoading } = useEnsureMessageThread(technicianId, {
    enabled: messagePanel.open && !!technicianId,
  });

  const activeThreadId = ensuredThread?.id || threads.find((t) => t.technicianId === technicianId)?.id;

  if (!messagePanel.open) return null;

  const handleClose = () => {
    closeMessagePanel();
  };

  const handleSelectThread = (thread) => {
    openMessageThread({
      technicianId: thread.technicianId,
      technician: thread.technician,
    });
  };

  return (
    <div className="fixed inset-0 z-[90] flex justify-end">
      <button
        type="button"
        className="absolute inset-0 bg-gray-900/30"
        aria-label="Close messages"
        onClick={handleClose}
      />

      <div className="relative w-full max-w-md h-full bg-surface border-l border-surface-border shadow-xl flex flex-col">
        {technicianId ? (
          ensureLoading && !activeThreadId ? (
            <>
              <header className="flex items-center justify-between px-4 py-3 border-b border-surface-border flex-shrink-0">
                <div className="flex items-center gap-2">
                  <MessageSquare className="h-5 w-5 text-primary-600" />
                  <h2 className="text-sm font-semibold text-gray-900">Messages</h2>
                </div>
                <button
                  type="button"
                  onClick={handleClose}
                  className="p-1.5 rounded-md hover:bg-surface-hover text-gray-500"
                >
                  <X className="h-4 w-4" />
                </button>
              </header>
              <div className="flex-1 flex items-center justify-center text-sm text-gray-500">
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
                Opening conversation…
              </div>
            </>
          ) : (
            <ThreadConversation
              threadId={activeThreadId}
              technician={selectedTechnician}
              serviceCallId={messagePanel.serviceCallId}
              serviceCallLabel={messagePanel.serviceCallLabel}
              onBack={threads.length > 0 ? () => openMessageInbox() : null}
              onClose={handleClose}
            />
          )
        ) : (
          <>
            <header className="flex items-center justify-between px-4 py-3 border-b border-surface-border flex-shrink-0">
              <div className="flex items-center gap-2">
                <MessageSquare className="h-5 w-5 text-primary-600" />
                <div>
                  <h2 className="text-sm font-semibold text-gray-900">Messages</h2>
                  <p className="text-2xs text-gray-500">Office ↔ technician chat</p>
                </div>
              </div>
              <button
                type="button"
                onClick={handleClose}
                className="p-1.5 rounded-md hover:bg-surface-hover text-gray-500"
                aria-label="Close messages"
              >
                <X className="h-4 w-4" />
              </button>
            </header>

            {threadsLoading ? (
              <div className="flex-1 flex items-center justify-center text-sm text-gray-500">
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
                Loading conversations…
              </div>
            ) : (
              <MessageThreadList
                threads={threads}
                onSelect={handleSelectThread}
              />
            )}

            {showInbox && (
              <div className="px-4 py-3 border-t border-surface-border text-2xs text-gray-400 flex-shrink-0">
                Tip: use the message icon on a technician in the Tech Dock to start a new chat.
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
